import React, { useRef, useCallback, useState, useEffect } from 'react';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import { CircularProgress, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatterCurrency } from '../../../utils/methods';
import useUserAuth from '../../../utils/useUserAuth';
import { getOrderPaymentLines, getOrderRefundLines } from '../../../api/payment';
import { SPLIT_METHOD_LABEL } from '../../SalesManagment/modal/enterSaleInfoModal/SplitPaymentSection';
import SellerNameCell from '../../common/SellerNameCell';
import {
  PaymentTableContainer,
  PaymentTable,
  PaymentHead,
  PaymentHeadRow,
  PaymentHeadCell,
  PaymentBodyRow,
  PaymentBodyCell,
  TicketNameCell,
  RowActions,
  ViewButton,
  EditButton,
  StatusChip,
  EmptyCell,
} from './UserPaymentHistoryStyled';

// 컬럼 정의 — 성격별 그룹핑: 상품 → 금액 → 결제 → 환불 → 기타
const COLUMNS = [
  // 상품 정보
  { key: '거래일시', width: '170px', raw: true },
  { key: '구분', width: '80px', raw: true },
  { key: '회원권명', width: '240px', raw: true },
  // 금액 정보
  { key: 'Sales Price', width: '100px' },
  { key: 'Discounted Price', width: '100px' },
  { key: '포인트사용', width: '100px', raw: true },
  { key: 'payment', width: '120px' },
  { key: 'Outstanding Amount', width: '100px' },
  // 환불·양수 정보 — 결제상태 바로 앞에 모아 둔다(매출관리 전체내역과 동일 배치).
  { key: 'Refund Method', width: '100px' },
  { key: 'Refund Amount', width: '100px' },
  { key: '양도 수수료', width: '110px', raw: true },
  // 결제 정보
  { key: 'Payment Status' },
  { key: 'Payment Method' },
  { key: 'Payment Provider' },
  { key: 'Payment Detail', width: '300px' },
  // 기타
  { key: 'Sale Manager' },
  { key: 'Memo' },
];

// 양수금(양도 수수료) 행 판별 — 매출관리와 동일 기준(payType='TRANSFER_FEE').
const isTransferFeeRow = row => row.payType === 'TRANSFER_FEE' || row.payTypeName === '양수금';

// 환불행 판별 — 이원화로 환불 건은 결제행/환불행 2행이 되고, 환불행은 rowType='REFUND'.
const isRefundRow = row => row.rowType === 'REFUND' || row.paymentStatusName === '환불';

// 분할결제(#49) — 주문 레벨 수단이 'MIXED' 인 행. 표시는 '분할결제', 갈매기로 라인을 펼친다.
const MIXED_METHOD = 'MIXED';
const methodLabel = m => (m === MIXED_METHOD ? '분할결제' : m);
const isMixedRow = row =>
  isRefundRow(row) ? row.refundMethod === MIXED_METHOD : row.paymentMethod === MIXED_METHOD;
const mixedRowKey = row => `${row.rowType || 'PAY'}-${row.memberOrderId}`;

// 라인 일시 — 결제 라인은 PAYMENT_DATE, 환불 라인은 부모 환불행 일시를 따른다.
const splitLineDateText = (line, row) => String(line.paymentDate || row.paidDate || '').replace('T', ' ').slice(0, 16);

// 라인 상세 텍스트 — 매출관리 갈매기 하위 행과 동일 표기.
const splitLineDetailText = line => {
  const parts = [];
  if (line.cardCompany) parts.push(line.cardNumber ? `${line.cardCompany}(${line.cardNumber})` : line.cardCompany);
  const approval = line.cid || line.cId; // Jackson 은 cId 를 'cid' 키로 직렬화한다
  if (approval) parts.push(`승인번호 ${approval}`);
  if (line.bankInfo) parts.push(line.bankInfo);
  if (line.depositorName) parts.push(line.depositorName);
  return parts.join(' · ') || '-';
};

// 행 배경 톤 — 매출관리와 동일 규칙. 환불이 미수금보다 강한 신호라 우선한다.
const rowToneOf = row => {
  if (isRefundRow(row)) return 'refund';
  if (Number(row.outstandingAmount) > 0) return 'outstanding';
  return undefined;
};

// 칩 상태 — 매출관리와 같은 규칙으로 "그 행이 무엇인지"를 색으로 나타낸다.
// 주문 전체의 paymentStatus 를 그대로 쓰면 환불된 주문의 '결제행'까지 붉게 나오므로,
// 환불행만 REFUNDED 로 잡고 결제행은 실제 결제 성격(PAID/PAID_CHANGE)을 따른다.
const chipStatusOf = row => {
  if (isRefundRow(row)) return 'REFUNDED';
  if (row.paymentStatus === 'CANCELED') return 'CANCELED';
  if (Number(row.outstandingAmount) > 0) return 'OUTSTANDING';
  if (row.paymentStatus === 'PAID_CHANGE') return 'PAID_CHANGE';
  if (row.paymentStatus === 'PAID_OUTSTANDING') return 'PAID_OUTSTANDING';
  return 'PAID';
};

const isRefundedOrCanceled = status => status === 'REFUNDED' || status === 'CANCELED';

const UserPaymentHistoryTable = ({ rows, onClickEdit, onClickRefundDetail, onClickPayDetail }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const canPaymentEdit = useUserAuth('MEMBER_PAYMENT_EDIT');
  const canPaymentDetailEdit = useUserAuth('MEMBER_PAYMENT_DETAIL_EDIT');

  // 분할결제 행 갈매기 펼침 — 결제행은 수납 라인, 환불행은 환불 라인 (매출관리 전체 내역과 동일).
  const [expandedKey, setExpandedKey] = useState(null);
  const [lineCache, setLineCache] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    setExpandedKey(null);
    setLineCache({});
    setLoadingKey(null);
  }, [rows]);

  const toggleMixedRow = async row => {
    const key = mixedRowKey(row);
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (lineCache[key] === undefined) {
      setLoadingKey(key);
      try {
        const fetcher = isRefundRow(row) ? getOrderRefundLines : getOrderPaymentLines;
        const lines = await fetcher(row.memberOrderId, user.token);
        setLineCache(prev => ({ ...prev, [key]: Array.isArray(lines) ? lines : [] }));
      } catch (e) {
        setLineCache(prev => ({ ...prev, [key]: [] }));
      } finally {
        setLoadingKey(null);
      }
    }
  };

  // 거래일시 셀 — 분할결제(펼침 가능) 행이면 날짜 옆 갈매기 토글(수단 셀은 일반 텍스트) — 사용자 결정.
  const paidDateCellContent = row => {
    if (!isMixedRow(row)) return row.paidDate;
    const expanded = expandedKey === mixedRowKey(row);
    // 갈매기는 날짜 오른쪽에 절대배치 — 날짜 텍스트 시작 위치가 일반 행과 동일하게 유지된다.
    return (
      <span
        onClick={e => {
          e.stopPropagation(); // 행 전체 클릭 토글과 이중 발화 방지
          toggleMixedRow(row);
        }}
        style={{ cursor: 'pointer', position: 'relative', display: 'inline-block' }}>
        {row.paidDate}
        {expanded ? (
          <KeyboardArrowUpIcon
            sx={{ fontSize: 16, color: '#4ab3bc', position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)', ml: '4px' }}
          />
        ) : (
          <KeyboardArrowDownIcon
            sx={{ fontSize: 16, color: '#4ab3bc', position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)', ml: '4px' }}
          />
        )}
      </span>
    );
  };

  // 드래그 스크롤
  const containerRef = useRef(null);
  const dragState = useRef({ isDragging: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });

  const handleMouseDown = useCallback(e => {
    // 버튼 클릭은 드래그로 처리하지 않음
    if (e.target.closest('button')) return;
    const el = containerRef.current;
    dragState.current = { isDragging: true, moved: false, startX: e.clientX, startY: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback(e => {
    if (!dragState.current.isDragging) return;
    const { startX, startY, scrollLeft, scrollTop } = dragState.current;
    if (Math.abs(e.clientX - startX) > 4 || Math.abs(e.clientY - startY) > 4) {
      dragState.current.moved = true;
    }
    containerRef.current.scrollLeft = scrollLeft - (e.clientX - startX);
    containerRef.current.scrollTop = scrollTop - (e.clientY - startY);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!dragState.current.isDragging) return;
    dragState.current.isDragging = false;
    const el = containerRef.current;
    el.style.cursor = 'grab';
    el.style.userSelect = '';
  }, []);

  return (
    <PaymentTableContainer
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      sx={{ cursor: 'grab' }}>
      <PaymentTable size="small">
        <PaymentHead>
          <PaymentHeadRow>
            {COLUMNS.map(col => (
              <PaymentHeadCell key={col.key} align="center" width={col.width}>
                {col.raw ? col.key : t(col.key)}
              </PaymentHeadCell>
            ))}
          </PaymentHeadRow>
        </PaymentHead>

        <TableBody>
          {(!rows || rows.length === 0) && (
            <TableRow>
              <EmptyCell colSpan={COLUMNS.length}>결제 내역이 없습니다</EmptyCell>
            </TableRow>
          )}
          {rows?.map((row, index) => (
            <React.Fragment key={index}>
            {/* 분할결제 행은 행 어디를 눌러도 펼침 토글(버튼·드래그 스크롤은 제외). */}
            <PaymentBodyRow
              canceled={row.paymentStatus === 'CANCELED'}
              rowTone={rowToneOf(row)}
              onClick={
                isMixedRow(row)
                  ? e => {
                      if (dragState.current.moved || e.target.closest('button')) return;
                      toggleMixedRow(row);
                    }
                  : undefined
              }
              sx={
                {
                  // 부모 행 배경은 일반 행과 동일(흰/rowTone) — 자식 라인 색으로만 분할을 구분한다(사용자 결정)
                  ...(isMixedRow(row)
                  ? {
                      cursor: 'pointer',
                      // MUI 셀 배경이 row 그림자를 가리므로 셀 border 로 묶음 외곽(위/좌/우)을 그린다 — 환불 행은 붉은 계열
                      ...(expandedKey === mixedRowKey(row)
                        ? (() => {
                            const edge = isRefundRow(row) ? '#E08A80' : '#4AB3BC';
                            return {
                              '& > td': { borderTop: `2px solid ${edge}` },
                              '& > td:first-of-type': { borderLeft: `2px solid ${edge}` },
                              '& > td:last-of-type': { borderRight: `2px solid ${edge}` },
                            };
                          })()
                        : {}),
                    }
                  : {}),
                }
              }>
              {/* 상품 정보 */}
              <PaymentBodyCell align="center">{paidDateCellContent(row)}</PaymentBodyCell>
              <PaymentBodyCell align="center">{row.payTypeName}</PaymentBodyCell>
              <TicketNameCell align="center">
                <span>{row.courseTicketName}</span>
                <RowActions className="row-actions">
                  {isRefundedOrCanceled(row.paymentStatus) ? (
                    <ViewButton variant="contained" size="small" onClick={() => onClickRefundDetail(row)}>
                      환불내역
                    </ViewButton>
                  ) : (
                    <Tooltip title={!canPaymentEdit ? '권한이 없습니다' : ''} arrow>
                      <EditButton
                        variant="contained"
                        size="small"
                        onClick={canPaymentEdit ? () => onClickEdit(row) : undefined}
                        sx={!canPaymentEdit ? { opacity: 0.5, cursor: 'default' } : {}}>
                        결제수정
                      </EditButton>
                    </Tooltip>
                  )}
                  <Tooltip title={!canPaymentDetailEdit ? '권한이 없습니다' : ''} arrow>
                    <ViewButton
                      variant="contained"
                      size="small"
                      onClick={canPaymentDetailEdit ? () => onClickPayDetail(row) : undefined}
                      sx={!canPaymentDetailEdit ? { opacity: 0.5, cursor: 'default' } : {}}>
                      판매정보
                    </ViewButton>
                  </Tooltip>
                </RowActions>
              </TicketNameCell>
              {/* 금액 정보 */}
              <PaymentBodyCell align="center">{formatterCurrency(row.regularPrice)}</PaymentBodyCell>
              <PaymentBodyCell align="center">{formatterCurrency(row.discountPrice)}</PaymentBodyCell>
              <PaymentBodyCell align="center">{formatterCurrency(row.usedPoint)}</PaymentBodyCell>
              <PaymentBodyCell align="center">{formatterCurrency(row.finalPrice)}</PaymentBodyCell>
              <PaymentBodyCell align="center">{formatterCurrency(row.outstandingAmount)}</PaymentBodyCell>
              {/* 환불·양수 정보 */}
              {/* 분할결제 환불은 갈매기(결제수단 셀)로 펼쳐 보므로 '-' 표기 */}
              <PaymentBodyCell align="center">
                {row.refundMethod && row.refundMethod !== MIXED_METHOD ? t(methodLabel(row.refundMethod)) : '-'}
              </PaymentBodyCell>
              <PaymentBodyCell align="center">{formatterCurrency(row.refundAmount)}</PaymentBodyCell>
              {/* 양도 수수료 — 양수금 행만 결제금액을 수수료로 표시, 그 외는 '-' */}
              <PaymentBodyCell align="center">
                {isTransferFeeRow(row) ? formatterCurrency(row.finalPrice) : '-'}
              </PaymentBodyCell>
              {/* 결제 정보 */}
              <PaymentBodyCell align="center">
                <StatusChip status={chipStatusOf(row)}>
                  {row.outstandingAmount > 0 && !isRefundRow(row) ? '미수금' : t(row.paymentStatusName === '변경결제' ? '결제' : row.paymentStatusName)}
                </StatusChip>
              </PaymentBodyCell>
              <PaymentBodyCell align="center">{t(methodLabel(row.paymentMethod))}</PaymentBodyCell>
              <PaymentBodyCell align="center">{row.paymentProviderName}</PaymentBodyCell>
              <PaymentBodyCell align="center">{row.payDetail || '-'}</PaymentBodyCell>
              {/* 기타 */}
              <PaymentBodyCell align="center"><SellerNameCell managerName={row.managerName} fallback="" /></PaymentBodyCell>
              <Tooltip
                title={row.memo || ''}
                arrow
                placement="top"
                disableHoverListener={!row.memo}
                componentsProps={{
                  tooltip: {
                    sx: { fontSize: '13px', maxWidth: '300px', padding: '8px 12px', lineHeight: 1.5 },
                  },
                }}>
                <PaymentBodyCell align="center">{row.memo}</PaymentBodyCell>
              </Tooltip>
            </PaymentBodyRow>
            {expandedKey === mixedRowKey(row) &&
              (loadingKey === mixedRowKey(row) ? (
                <TableRow>
                  <PaymentBodyCell colSpan={COLUMNS.length} align="center" sx={{ background: '#E3F2F4' }}>
                    <CircularProgress size={18} />
                  </PaymentBodyCell>
                </TableRow>
              ) : (
                (lineCache[mixedRowKey(row)] || []).map((line, li, arr) => {
                  const isLast = li === arr.length - 1;
                  const refund = isRefundRow(row);
                  const methodName = line.paymentMethod || line.refundMethod;
                  const methodText = SPLIT_METHOD_LABEL[methodName] || methodName;
                  // 부모는 흰색 — 자식 라인 색으로 분할 묶음을 구분한다
                  const subBg = { background: refund ? '#F9E4E1' : '#E3F2F4' };
                  const edge = refund ? '#E08A80' : '#4AB3BC';
                  const amountText = refund ? `- ${formatterCurrency(line.amount)}` : formatterCurrency(line.amount);
                  // 부모 컬럼(거래일시/구분/회원권명/판매가/할인/포인트/결제금액/미수금/환불수단/환불금액/양도/상태/수단/경로/상세/담당자/메모) 1:1 정렬
                  return (
                    <TableRow
                      key={`${mixedRowKey(row)}-line-${li}`}
                      sx={{
                        '& > td:first-of-type': { borderLeft: `2px solid ${edge}` },
                        '& > td:last-of-type': { borderRight: `2px solid ${edge}` },
                        ...(isLast ? { '& > td': { borderBottom: `2px solid ${edge}` } } : {}),
                      }}>
                      <PaymentBodyCell align="center" sx={subBg}>{splitLineDateText(line, row)}</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>{row.payTypeName}</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>{row.courseTicketName}</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>-</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>-</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>-</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>{refund ? '-' : amountText}</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>-</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>{refund ? methodText : '-'}</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={{ ...subBg, color: refund ? '#e05b5b' : undefined }}>
                        {refund ? amountText : '-'}
                      </PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>-</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>
                        <StatusChip status={chipStatusOf(row)}>
                          {row.outstandingAmount > 0 && !refund ? '미수금' : t(row.paymentStatusName === '변경결제' ? '결제' : row.paymentStatusName)}
                        </StatusChip>
                      </PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={{ ...subBg, fontWeight: 700 }}>
                        {refund ? '-' : methodText}
                      </PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>{row.paymentProviderName || '-'}</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>{splitLineDetailText(line)}</PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}><SellerNameCell managerName={row.managerName} /></PaymentBodyCell>
                      <PaymentBodyCell align="center" sx={subBg}>-</PaymentBodyCell>
                    </TableRow>
                  );
                })
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </PaymentTable>
    </PaymentTableContainer>
  );
};

export default UserPaymentHistoryTable;
